/**
 * Enumerates the questions that can be RECODE targets.
 *
 * `parseLabelsAndLists` only captures visible `Q LABEL:` lines, but the most
 * common RECODE targets are HIDDEN questions. Rather than widen labelUtils
 * (which feeds the Logic Wizard, structure panel, and labels modal), we scan
 * here for `HIDDEN <name>` lines, build a synthetic QUESTION label per hidden
 * var, and route everything through `detectQuestionInfo` — which records the
 * underlying selectKind/fillKind even when the type collapses to HIDDEN.
 */
import { LabelItem, LabelType } from "../labelUtils"
import {
  detectQuestionInfo,
  QuestionInfo,
} from "../logicWizard/questionTypeDetector"

export interface RecodeTargets {
  /** Visible Q + HIDDEN questions, all typed QUESTION (feed to QuestionPicker). */
  labels: LabelItem[]
  /** QuestionInfo indexed by target name. */
  info: Map<string, QuestionInfo>
}

// `HIDDEN <name>` with an optional trailing colon (templates use `HIDDEN x:`).
const HIDDEN_RE = /^HIDDEN\s+(.+?)\s*:?\s*$/i

export function collectRecodeTargets(
  plainText: string,
  labels: LabelItem[],
): RecodeTargets {
  const lines = plainText.split("\n")

  const listMap = new Map<string, LabelItem>()
  for (const l of labels) {
    if (l.type === LabelType.LIST || l.type === LabelType.RANGE_LIST) {
      listMap.set(l.name, l)
    }
  }

  const resultLabels: LabelItem[] = []
  const info = new Map<string, QuestionInfo>()
  const seen = new Set<string>()

  // Visible questions, as parsed by labelUtils.
  for (const l of labels) {
    if (l.type !== LabelType.QUESTION || seen.has(l.name)) continue
    seen.add(l.name)
    resultLabels.push(l)
    info.set(l.name, detectQuestionInfo(l, lines, listMap))
  }

  // HIDDEN questions, scanned directly.
  lines.forEach((line, i) => {
    const m = line.match(HIDDEN_RE)
    if (!m) return
    const name = m[1].trim()
    if (!name || seen.has(name)) return
    seen.add(name)
    const synthetic: LabelItem = {
      id: `QUESTION_${name.replace(/\s+/g, "_")}`,
      type: LabelType.QUESTION,
      name,
      content: line.trim(),
      lineNumbers: [i + 1],
      originalLines: [line],
    }
    resultLabels.push(synthetic)
    info.set(name, detectQuestionInfo(synthetic, lines, listMap))
  })

  return { labels: resultLabels, info }
}
